/**
 * Location Store
 * Manages location state and geolocation
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { getDistance, type ROLocation } from "rainowls-shared";

export interface SavedLocation extends ROLocation {
  id: string;
  isPinned: boolean;
  addedAt: Date;
}

export const DEFAULT_LOCATION: ROLocation = {
  id: "default-portland",
  lat: 45.5152,
  lng: -122.6784,
  name: "Portland",
  city: "Portland",
  country: "United States",
  timeZone: "America/Los_Angeles",
};

export interface LocationHeaderData {
  selected: ROLocation;
  savedLocations: SavedLocation[];
  selectedLocationId: string | null;
  isGeolocating: boolean;
  geolocationError: string | null;
  canAddLocation: boolean;
  maxSavedLocations: number;
}

const STORAGE_KEY = "rainowls-saved-locations";
const MAX_SAVED_LOCATIONS = 10;
const CURRENT_LOCATION_MATCH_METERS = 500;
export const MAX_LOCATION_NAME_LENGTH = 64;

/** Only well-formed persisted locations may enter the store or request URLs. */
export function normalizeSavedLocations(value: unknown): SavedLocation[] {
  if (!Array.isArray(value)) return [];
  const ids = new Set<string>();
  return (
    value
      .flatMap((raw): SavedLocation[] => {
        if (!raw || typeof raw !== "object") return [];
        const { id, lat, lng } = raw;
        if (
          typeof id !== "string" ||
          !id ||
          ids.has(id) ||
          typeof lat !== "number" ||
          !Number.isFinite(lat) ||
          Math.abs(lat) > 90 ||
          typeof lng !== "number" ||
          !Number.isFinite(lng) ||
          Math.abs(lng) > 180
        )
          return [];
        ids.add(id);
        const text = (key: string) =>
          typeof raw[key] === "string" ? raw[key] : "";
        const date = new Date(
          typeof raw.addedAt === "string" ? raw.addedAt : 0,
        );
        return [
          {
            id,
            lat,
            lng,
            name: text("name"),
            city: text("city"),
            country: text("country"),
            timeZone: text("timeZone"),
            isPinned: raw.isPinned === true,
            addedAt: Number.isFinite(date.getTime()) ? date : new Date(0),
          },
        ];
      })
      // Preserve previously saved entries above the new limit; block new saves.
      .slice(0, 20)
  );
}

export const useLocationStore = defineStore("location", () => {
  // State
  const currentLocation = ref<ROLocation | null>(null);
  const savedLocations = ref<SavedLocation[]>([]);
  const selectedLocationId = ref<string | null>(null);
  const previewLocation = ref<ROLocation | null>(null);
  const isGeolocating = ref(false);
  const geolocationError = ref<string | null>(null);
  const selectionVersion = ref(0);
  let geolocationRequest: Promise<ROLocation | null> | null = null;

  // Getters
  const selectedLocation = computed((): ROLocation => {
    if (previewLocation.value) return previewLocation.value;
    if (!selectedLocationId.value) {
      return currentLocation.value ?? DEFAULT_LOCATION;
    }
    const saved = savedLocations.value.find(
      (l) => l.id === selectedLocationId.value,
    );
    return saved ?? currentLocation.value ?? DEFAULT_LOCATION;
  });

  const displayLocation = computed<ROLocation>(() => selectedLocation.value);

  const canAddLocation = computed(
    () => savedLocations.value.length < MAX_SAVED_LOCATIONS,
  );

  const headerData = computed<LocationHeaderData>(() => ({
    selected: selectedLocation.value,
    savedLocations: savedLocations.value,
    selectedLocationId: selectedLocationId.value,
    isGeolocating: isGeolocating.value,
    geolocationError: geolocationError.value,
    canAddLocation: canAddLocation.value,
    maxSavedLocations: MAX_SAVED_LOCATIONS,
  }));

  /** Preview a searched place without saving it. */
  function viewLocation(location: ROLocation): void {
    const existing = savedLocations.value.find(
      (saved) =>
        Math.abs(saved.lat - location.lat) < 0.0001 &&
        Math.abs(saved.lng - location.lng) < 0.0001,
    );
    selectLocation(existing?.id ?? null);
    if (!existing) previewLocation.value = { ...location };
  }

  /** Save a new place, or select the existing place at those coordinates. */
  function saveAndSelect(location: ROLocation): void {
    const existing = savedLocations.value.find(
      (saved) =>
        Math.abs(saved.lat - location.lat) < 0.0001 &&
        Math.abs(saved.lng - location.lng) < 0.0001,
    );
    const saved = existing ?? addSavedLocation(location);
    if (saved) selectLocation(saved.id);
  }

  /** Match GPS jitter to a saved place within the accepted radius. */
  function nearestSavedLocation(
    location: ROLocation,
  ): SavedLocation | undefined {
    let nearest: SavedLocation | undefined;
    let nearestMeters = CURRENT_LOCATION_MATCH_METERS;
    for (const saved of savedLocations.value) {
      const meters =
        getDistance(location.lat, location.lng, saved.lat, saved.lng) *
        1609.344;
      if (meters <= nearestMeters) {
        nearest = saved;
        nearestMeters = meters;
      }
    }
    return nearest;
  }

  /** Resolve GPS and select the nearby saved place if the user has not moved on. */
  async function locateAndSelect(): Promise<void> {
    const version = selectionVersion.value;
    const location = await getCurrentLocation();
    if (location && version === selectionVersion.value) {
      // Publish together so watchers never fetch the jittered point before its match.
      currentLocation.value = location;
      selectLocation(nearestSavedLocation(location)?.id ?? null);
    }
  }

  /** Coalesce concurrent browser geolocation requests. */
  function getCurrentLocation(): Promise<ROLocation | null> {
    if (!geolocationRequest) {
      geolocationRequest = resolveCurrentLocation().finally(() => {
        geolocationRequest = null;
      });
    }
    return geolocationRequest;
  }

  /** Request browser coordinates and use reverse geocoding for a readable name. */
  async function resolveCurrentLocation(): Promise<ROLocation | null> {
    if (!navigator.geolocation) {
      geolocationError.value = "Geolocation is not supported by this browser";
      return null;
    }

    isGeolocating.value = true;
    geolocationError.value = null;

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location: ROLocation = {
            id: `current-${Date.now()}`,
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            name: "Current Location",
            country: "",
            city: "",
          };

          // Try to reverse geocode for a better name
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${location.lat}&lon=${location.lng}&format=json`,
              { signal: AbortSignal.timeout(4000) },
            );
            const data = await response.json();
            if (data.address) {
              const city =
                data.address.city ||
                data.address.town ||
                data.address.village ||
                "";
              location.name = city || "Current Location";
              location.country = data.address.country || "";
              location.city = city;
            }
          } catch {
            // Keep default name if reverse geocoding fails
          }

          isGeolocating.value = false;
          resolve(location);
        },
        (error) => {
          isGeolocating.value = false;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              geolocationError.value = "Location access denied";
              break;
            case error.POSITION_UNAVAILABLE:
              geolocationError.value = "Location unavailable";
              break;
            case error.TIMEOUT:
              geolocationError.value = "Location request timed out";
              break;
            default:
              geolocationError.value = "An unknown error occurred";
          }
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        },
      );
    });
  }

  /** Apply a selection without counting it as a new user choice. */
  function applySelection(locationId: string | null): void {
    previewLocation.value = null;
    selectedLocationId.value = locationId;
  }

  /** Select a saved place or the current location. */
  function selectLocation(locationId: string | null): void {
    selectionVersion.value++;
    applySelection(locationId);
  }

  /** Resolve the startup location while honoring any intervening user choice. */
  async function initializeSelection(): Promise<void> {
    const version = selectionVersion.value;
    const current = await getCurrentLocation();
    // A user selection made during geolocation always wins.
    if (version !== selectionVersion.value) return;
    const fallback = savedLocations.value[0];
    if (current) {
      currentLocation.value = current;
      applySelection(nearestSavedLocation(current)?.id ?? null);
    } else {
      // Use the first saved place, or Portland when there is no saved place.
      applySelection(fallback?.id ?? null);
    }
  }

  /** Add a place only while the saved-location limit permits it. */
  function addSavedLocation(
    location: ROLocation,
    pinned = false,
  ): SavedLocation | null {
    if (!canAddLocation.value) {
      return null;
    }

    const savedLocation: SavedLocation = {
      ...location,
      id: `${location.lat}-${location.lng}-${Date.now()}`,
      isPinned: pinned,
      addedAt: new Date(),
    };

    savedLocations.value.push(savedLocation);
    persistLocations();
    return savedLocation;
  }

  /** Remove a place and select an available fallback if it was active. */
  function removeSavedLocation(id: string): void {
    savedLocations.value = savedLocations.value.filter((l) => l.id !== id);
    if (selectedLocationId.value === id) {
      selectLocation(
        currentLocation.value ? null : (savedLocations.value[0]?.id ?? null),
      );
    }
    persistLocations();
  }

  /** Rename a saved place without changing its weather coordinates. */
  function renameSavedLocation(id: string, name: string): boolean {
    const trimmed = name.trim();
    if (
      !trimmed ||
      trimmed.length > MAX_LOCATION_NAME_LENGTH ||
      !savedLocations.value.some((location) => location.id === id)
    )
      return false;
    savedLocations.value = savedLocations.value.map((location) =>
      location.id === id ? { ...location, name: trimmed } : location,
    );
    persistLocations();
    return true;
  }

  /** Write saved places to local storage after a user edit. */
  function persistLocations(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedLocations.value));
    } catch {
      console.error("Failed to persist locations");
    }
  }

  /** Restore validated saved places at store initialization. */
  function loadSavedLocations(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        savedLocations.value = normalizeSavedLocations(JSON.parse(stored));
      }
    } catch {
      console.error("Failed to load saved locations");
    }
  }

  // Initialize saved locations from storage
  loadSavedLocations();

  return {
    // State
    headerData,
    saveAndSelect,
    viewLocation,
    locateAndSelect,
    currentLocation,
    savedLocations,
    selectedLocationId,
    isGeolocating,
    geolocationError,

    // Getters
    selectedLocation,
    displayLocation,
    selectionVersion,
    canAddLocation,
    maxSavedLocations: MAX_SAVED_LOCATIONS,

    // Actions
    getCurrentLocation,
    initializeSelection,
    selectLocation,
    addSavedLocation,
    removeSavedLocation,
    renameSavedLocation,
    loadSavedLocations,
  };
});
